import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PhoneIcon, XIcon } from 'lucide-react';
import type { Lead } from '../../types';
type Outcome = 'Connected' | 'No Answer' | 'Voicemail';
interface CallDialogProps {
  open: boolean;
  lead: Lead;
  onClose: () => void;
  onLog: (outcome: Outcome) => void;
}
const outcomes: {
  value: Outcome;
  help: string;
}[] = [{
  value: 'Connected',
  help: 'Spoke with the borrower'
}, {
  value: 'No Answer',
  help: 'No pickup, no voicemail left'
}, {
  value: 'Voicemail',
  help: 'Left a voicemail message'
}];
export function CallDialog({
  open,
  lead,
  onClose,
  onLog
}: CallDialogProps) {
  return <AnimatePresence data-id="element-137" data-mp="2r97m2op03q8r">
      {open ? <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Log call outcome" data-id="element-138" data-mp="2n9ypzd6hnga1">
          <motion.div className="absolute inset-0 bg-ink-900/25" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-139" data-mp="1buif4bhj9ijd" />
          <motion.div initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 340,
        damping: 30
      }} className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-lift" data-id="element-140" data-mp="12kpfe7pnzzdc">
            <div className="flex items-center gap-3" data-id="element-141" data-mp="eu26bj40j3p7">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-700" data-id="element-142" data-mp="jmlh68bbfkqb">
                <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-143" data-mp="4yid54gu2j45" />
              </span>
              <div data-id="element-144" data-mp="9vigv8q7n9s3">
                <h2 className="text-base font-semibold text-ink-900" data-id="element-145" data-mp="9wtmle37urlu">Calling {lead.name}</h2>
                <p className="tabular text-sm text-ink-500" data-id="element-146" data-mp="3s37xf466ulg0">{lead.phone}</p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between" data-id="element-147" data-mp="v2ey3p48apc">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-500" data-id="element-148" data-mp="22lt8yw0w3ht9">
                Log the outcome
              </p>
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-inset ring-amber-100" data-id="element-149" data-mp="1xryfx1ze9qhi">
                Disposition required
              </span>
            </div>
            <div className="mt-3 space-y-2" data-id="element-150" data-mp="1orjvs5y62hfl">
              {outcomes.map(outcome => <button key={outcome.value} type="button" onClick={() => onLog(outcome.value)} className="flex w-full items-center justify-between rounded-xl border border-ink-200 px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50" data-id="element-151" data-mp="2lh9kyvns271g">
                  <span data-id="element-152" data-mp="114ts70666esv">
                    <span className="block text-sm font-medium text-ink-900" data-id="element-153" data-mp="115d8kcg68v4s">{outcome.value}</span>
                    <span className="mt-0.5 block text-xs text-ink-500" data-id="element-154" data-mp="w34j3q2icb7f">{outcome.help}</span>
                  </span>
                </button>)}
            </div>

            <button type="button" onClick={onClose} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium text-ink-500 transition-colors hover:bg-ink-50 hover:text-ink-700" data-id="element-155" data-mp="3cs7180nl9iqw">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-156" data-mp="3qj22orvrcxkg" />
              Call not placed — close without logging
            </button>
          </motion.div>
        </div> : null}
    </AnimatePresence>;
}